<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-back-button default-href="/tabs/find-facilities" slot="start" />
        <ion-title>{{ translate("Add Store Configuration") }}</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <main>
        <ion-card>
          <ion-card-header>
            <ion-card-title>
              {{ translate("Product Stores") }}
            </ion-card-title>
            <ion-button v-if="selectedProductStores.length" @click="selectProductStore()" fill="clear">
              <ion-icon :icon="addCircleOutline" slot="start" />
              {{ translate("Add") }}
            </ion-button>
          </ion-card-header>
          <template v-if="selectedProductStores.length">
            <ion-list>
              <ion-item v-for="store in selectedProductStores" :key="store.productStoreId">
                <ion-label>
                  <h2>{{ getProductStore(store.productStoreId)?.storeName || store.productStoreId }}</h2>
                </ion-label>
                <!-- TODO add logic for make primary -->
                <ion-button id="product-store-actions-trigger" slot="end" fill="clear" color="medium">
                  <ion-icon slot="icon-only" :icon="ellipsisVerticalOutline" />
                </ion-button>
                <!-- inline popover as ProductStorePopover logic is complex and different from the use case here -->
                <ion-popover trigger="product-store-actions-trigger" showBackdrop="false" dismissOnSelect="true">
                  <ion-content>
                    <ion-list>
                      <ion-list-header>{{ getProductStore(store.productStoreId).storeName || store.productStoreId }}</ion-list-header>
                      <ion-item button lines="none" @click="removeProductStore(store.productStoreId)">
                        {{ translate("Unlink") }}
                        <ion-icon slot="end" :icon="removeCircleOutline" />
                      </ion-item>
                    </ion-list>
                  </ion-content>
                </ion-popover>
              </ion-item>
            </ion-list>
          </template>
          <ion-button v-else expand="block" fill="outline" @click="selectProductStore()">
            {{ translate("Add") }}
            <ion-icon slot="end" :icon="addCircleOutline" />
          </ion-button>
        </ion-card>

        <ion-card>
          <ion-card-header>
            <ion-card-title>
              {{ translate("Fulfillment Settings") }}
            </ion-card-title>
          </ion-card-header>
          <ion-list>
            <ion-item>
              <ion-toggle v-model="fulfillmentSettings.FAC_GRP">{{ translate("Sell Inventory Online") }}</ion-toggle>
            </ion-item>
            <ion-item>
              <ion-toggle v-model="fulfillmentSettings.PICKUP">{{ translate("Allow pickup") }}</ion-toggle>
            </ion-item>
            <ion-item>
              <ion-toggle v-model="fulfillmentSettings.OMS_FULFILLMENT" @ionChange="createLoginCreds = true">{{ translate("Uses native fulfillment app") }}</ion-toggle>
            </ion-item>
            <template v-if="fulfillmentSettings.OMS_FULFILLMENT">
              <ion-item>
                <ion-toggle v-model="createLoginCreds">{{ translate("Create login credentials") }}</ion-toggle>
              </ion-item>
              <template v-if="createLoginCreds">
                <ion-item>
                  <ion-input label-placement="floating" v-model="username">
                    <div slot="label">{{ translate('Username') }} <ion-text color="danger">*</ion-text></div>
                  </ion-input>
                </ion-item>
                <ion-item lines="none">
                  <ion-input label-placement="floating" v-model="password" ref="password" @keyup="validatePassword" @ionBlur="markPasswordTouched" type="password" helper-text="translate('Password should be at least 5 characters long, it contains at least one number, one alphabet and one special character.')">
                    <div slot="label">{{ translate('Password') }} <ion-text color="danger">*</ion-text></div>
                  </ion-input>
                </ion-item>
                <ion-item>
                  <ion-input label-placement="floating" v-model="emailAddress">
                    <div slot="label">{{ translate('Reset password email') }} <ion-text color="danger">*</ion-text></div>
                  </ion-input>
                </ion-item>
              </template>
            </template>
          </ion-list>
        </ion-card>

        <div class="ion-text-center ion-margin">
          <ion-button @click="saveStoreConfig()">
            <ion-icon slot="start" :icon="locationOutline"/>
            {{ translate("Save configurations") }}
          </ion-button>
          <ion-button @click="router.replace(`/facility-details/${facilityId}`)" color="medium" fill="clear">
            {{ translate("Configure settings later") }}
          </ion-button>
        </div>
      </main>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  IonBackButton,
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonPage,
  IonPopover,
  IonText,
  IonTitle,
  IonToolbar,
  IonToggle,
  modalController,
} from "@ionic/vue";
import { defineComponent } from "vue";
import { mapGetters, useStore } from "vuex";
import { useRouter } from 'vue-router'
import {
  addCircleOutline,
  ellipsisVerticalOutline,
  locationOutline,
  removeCircleOutline,
  star,
  starOutline
} from 'ionicons/icons';
import { translate } from "@hotwax/dxp-components";
import { isValidPassword, isValidEmail, showToast } from "@/utils";
import { hasError } from "@/adapter";
import logger from "@/logger";
import { FacilityService } from "@/services/FacilityService";
import { UserService } from "@/services/UserService"
import SelectProductStoreModal from '@/components/SelectProductStoreModal.vue';
import { DateTime } from "luxon";

export default defineComponent({
  name: "AddFacilityConfig",
  components: {
    IonBackButton,
    IonButton,
    IonCard,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonListHeader,
    IonPage,
    IonPopover,
    IonText,
    IonTitle,
    IonToggle,
    IonToolbar,
  },
  computed: {
    ...mapGetters({
      current: 'facility/getCurrent',
      getProductStore: 'util/getProductStore',
    })
  },
  data() {
    return {
      fulfillmentSettings: {
        PICKUP: false,
        FAC_GRP: false,
        OMS_FULFILLMENT: false
      } as any,
      createLoginCreds: false as any,
      password: '',
      username: '',
      emailAddress: '',
      selectedProductStores: [] as any,
    }
  },
  props: ['facilityId'],
  async ionViewWillEnter() {
    await this.store.dispatch('facility/fetchCurrentFacility', { facilityId: this.facilityId })
    await this.store.dispatch('util/fetchProductStores')
    this.username = this.current.facilityId
  },
  methods: {
    async saveFulfillmentSettings() {
      const responses = []
      if (this.fulfillmentSettings.PICKUP) {
        const resp = await FacilityService.addFacilityToGroup({
          "facilityId": this.facilityId,
          "facilityGroupId": 'PICKUP'
        })
        responses.push(resp)
      }

      if (this.fulfillmentSettings.FAC_GRP) {
        const resp = await FacilityService.addFacilityToGroup({
          "facilityId": this.facilityId,
          "facilityGroupId": 'FAC_GRP'
        })
        responses.push(resp)
      }

      if (this.fulfillmentSettings.OMS_FULFILLMENT) {
        const resp = await FacilityService.addFacilityToGroup({
          "facilityId": this.facilityId,
          "facilityGroupId": 'OMS_FULFILLMENT'
        })
        responses.push(resp)
      }

      const hasFailedResponse = responses.some((response: any) => hasError(response))
      if (hasFailedResponse) {
        throw { message: translate('Failed to update some fulfillment settings.') }
      }
    },
    async createFacilityLogin() {
      

      try {
        const payload = {
          "facilityId" : this.facilityId, 
          "facilityName": this.current.facilityName,
          "username": this.username,
          "password": this.password,
          "emailAddress": this.emailAddress
        }

        await FacilityService.createFacilityLogin(payload);
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async saveStoreConfig() {
      if (this.createLoginCreds) {

        if (!this.username ||  !this.password || !this.emailAddress) {
          showToast(translate('Please fill all the required fields'))
          return
        }
        if (this.username && await UserService.isUserLoginIdExists(this.username)) {
          showToast(translate('Could not create login user: user with ID already exists.', { userLoginId: this.username }))
          return;
        }
        if (!isValidEmail(this.emailAddress)) {
          showToast(translate('Please provide a valid email.'))
          return
        }
      }

      try {
        if (Object.values(this.fulfillmentSettings).includes(true)) {
          await this.saveFulfillmentSettings()
        }

        if (this.createLoginCreds) {
          await this.createFacilityLogin()
        }

        if (this.selectedProductStores) {
          await this.addProductStoresToFacility()
        }

        showToast(translate("Facility configurations created successfully."))
        this.$router.replace({ path: `/facility-details/${this.facilityId}` })
      } catch (error: any) {
        showToast(error.message)
        logger.error(error.message)
      }
    },
    async addProductStoresToFacility() {
      let responses = []
      for (const payload of this.selectedProductStores) {
        responses.push(await FacilityService.createProductStoreFacility({
          productStoreId: payload.productStoreId,
          facilityId: this.facilityId,
          fromDate: DateTime.now().toMillis(),
        }))
      }

      const hasFailedResponse = responses.some((response: any) => response.status === 'rejected')
      if (hasFailedResponse) {
        throw { message: translate('Failed to add some product stores to the facility.') }
      }
    },
    async selectProductStore() {
      const selectProductStoreModal = await modalController.create({
        component: SelectProductStoreModal,
        componentProps: {
          selectedProductStores: this.selectedProductStores
        }
      })

      selectProductStoreModal.onDidDismiss().then(async(result: any) => {
        if (result.data && result.data.value) {
          this.selectedProductStores = result.data.value.selectedProductStores
        }
      })

      selectProductStoreModal.present()
    },
    removeProductStore(productStoreId: string) {
      this.selectedProductStores = this.selectedProductStores.filter((store: any) => store.productStoreId !== productStoreId)
    },
    validatePassword(event: any) {
      const value = event.target.value;
      (this as any).$refs.password.$el.classList.remove('ion-valid');
      (this as any).$refs.password.$el.classList.remove('ion-invalid');

      if (value === '') return;

      isValidPassword(value)
        ? (this as any).$refs.password.$el.classList.add('ion-valid')
        : (this as any).$refs.password.$el.classList.add('ion-invalid');
    },
    markPasswordTouched() {
      (this as any).$refs.password.$el.classList.add('ion-touched');
    },
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    return {
      addCircleOutline,
      ellipsisVerticalOutline,
      locationOutline,
      removeCircleOutline,
      router,
      star,
      starOutline,
      store,
      translate
    };
  }
});
</script>

<style scoped>
ion-card-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

@media (min-width: 700px) {
  main {
    max-width: 375px;
    margin: auto;
  }
}
</style>